import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { 
  CognitoUser, 
  CognitoUserAttribute, 
  CognitoUserSession,
  AuthenticationDetails
} from 'amazon-cognito-identity-js';
import { AuthState, User, RegistrationFormData, LoginFormData, PasswordResetFormData } from '../types/auth';
import { userPool, createCognitoUser, createAuthenticationDetails } from '../utils/cognitoConfig';
import { mapCognitoError, createAuthError } from '../utils/errorHandler';
import { debugCognitoConfig, testCognitoConnection } from '../utils/cognitoDebug';

// Action types for the auth reducer
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_TOKENS'; payload: { accessToken: string | null; idToken: string | null; refreshToken: string | null } }
  | { type: 'CLEAR_AUTH' }
  | { type: 'SET_AUTHENTICATED'; payload: boolean };

// Initial auth state
const initialAuthState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  tokens: {
    accessToken: null,
    idToken: null,
    refreshToken: null
  }
};

// Auth reducer function
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload, 
        isAuthenticated: !!action.payload,
        error: null 
      };
    case 'SET_TOKENS':
      return { ...state, tokens: action.payload };
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
    case 'CLEAR_AUTH':
      return {
        ...initialAuthState,
        isLoading: false
      };
    default:
      return state;
  }
};

// Auth context interface
interface AuthContextType {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  tokens: {
    accessToken: string | null;
    idToken: string | null;
    refreshToken: string | null;
  };
  
  // Methods
  signUp: (userData: RegistrationFormData) => Promise<{ success: boolean; message?: string }>;
  signIn: (credentials: LoginFormData) => Promise<{ success: boolean; message?: string }>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string }>;
  confirmPassword: (data: PasswordResetFormData) => Promise<{ success: boolean; message?: string }>;
  getCurrentUser: () => Promise<User | null>;
  checkAuthentication: () => boolean;
  clearError: () => void;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token storage keys
const TOKEN_STORAGE_KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  ID_TOKEN: 'auth_id_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  USER_DATA: 'auth_user_data'
};

// Helper function to extract user data from Cognito session
const extractUserFromSession = (session: CognitoUserSession, cognitoUser: CognitoUser): User => {
  const idToken = session.getIdToken();
  const payload = idToken.payload;
  
  return {
    email: payload.email || cognitoUser.getUsername(),
    name: payload.name || payload.given_name || '',
    phoneNumber: payload.phone_number,
    emailVerified: payload.email_verified || false,
    createdAt: new Date(payload.iat * 1000).toISOString(),
    lastLoginAt: new Date().toISOString()
  };
};

// Helper function to store tokens securely
const storeTokens = (session: CognitoUserSession) => {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, session.getAccessToken().getJwtToken());
    localStorage.setItem(TOKEN_STORAGE_KEYS.ID_TOKEN, session.getIdToken().getJwtToken());
    localStorage.setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, session.getRefreshToken().getToken());
  } catch (error) {
    console.warn('Failed to store tokens in localStorage:', error);
  }
};

// Helper function to clear stored tokens
const clearStoredTokens = () => {
  try {
    Object.values(TOKEN_STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.warn('Failed to clear tokens from localStorage:', error);
  }
};

// Helper function to get stored tokens
const getStoredTokens = () => {
  try {
    return {
      accessToken: localStorage.getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN),
      idToken: localStorage.getItem(TOKEN_STORAGE_KEYS.ID_TOKEN),
      refreshToken: localStorage.getItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN)
    };
  } catch (error) {
    console.warn('Failed to retrieve tokens from localStorage:', error);
    return {
      accessToken: null,
      idToken: null,
      refreshToken: null
    };
  }
};

// AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  // Session validation and restoration
  const validateAndRestoreSession = async (): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        // No current user, check if we have stored tokens
        const storedTokens = getStoredTokens();
        if (!storedTokens.accessToken) {
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
        
        // Clear invalid stored tokens
        clearStoredTokens();
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      // Get session and validate
      cognitoUser.getSession(async (err: any, session: CognitoUserSession | null) => {
        if (err) {
          console.warn('Session validation error:', err);
          clearStoredTokens();
          dispatch({ type: 'CLEAR_AUTH' });
          return;
        }

        if (!session) {
          clearStoredTokens();
          dispatch({ type: 'CLEAR_AUTH' });
          return;
        }

        if (session.isValid()) {
          // Session is valid, restore user state
          const user = extractUserFromSession(session, cognitoUser);
          storeTokens(session);
          
          // Schedule automatic token refresh
          scheduleTokenRefresh(session);
          
          dispatch({ type: 'SET_USER', payload: user });
          dispatch({ type: 'SET_TOKENS', payload: {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken()
          }});
          dispatch({ type: 'SET_LOADING', payload: false });
        } else {
          // Session expired, try to refresh
          await refreshSession(cognitoUser, session);
        }
      });
    } catch (error) {
      console.warn('Error during session validation:', error);
      clearStoredTokens();
      dispatch({ type: 'CLEAR_AUTH' });
    }
  };

  // Token refresh function
  const refreshSession = async (cognitoUser: CognitoUser, session: CognitoUserSession): Promise<void> => {
    try {
      const refreshToken = session.getRefreshToken();
      
      cognitoUser.refreshSession(refreshToken, (err: any, newSession: CognitoUserSession) => {
        if (err) {
          console.warn('Token refresh failed:', err);
          clearStoredTokens();
          dispatch({ type: 'CLEAR_AUTH' });
          return;
        }

        if (newSession && newSession.isValid()) {
          // Successfully refreshed, update state
          const user = extractUserFromSession(newSession, cognitoUser);
          storeTokens(newSession);
          
          // Schedule next automatic token refresh
          scheduleTokenRefresh(newSession);
          
          dispatch({ type: 'SET_USER', payload: user });
          dispatch({ type: 'SET_TOKENS', payload: {
            accessToken: newSession.getAccessToken().getJwtToken(),
            idToken: newSession.getIdToken().getJwtToken(),
            refreshToken: newSession.getRefreshToken().getToken()
          }});
          dispatch({ type: 'SET_LOADING', payload: false });
        } else {
          clearStoredTokens();
          dispatch({ type: 'CLEAR_AUTH' });
        }
      });
    } catch (error) {
      console.warn('Error during token refresh:', error);
      clearStoredTokens();
      dispatch({ type: 'CLEAR_AUTH' });
    }
  };

  // Automatic token refresh before expiration
  const scheduleTokenRefresh = (session: CognitoUserSession): void => {
    const accessToken = session.getAccessToken();
    const expirationTime = accessToken.getExpiration() * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    
    // Refresh 5 minutes before expiration
    const refreshTime = Math.max(timeUntilExpiration - (5 * 60 * 1000), 0);
    
    if (refreshTime > 0) {
      setTimeout(async () => {
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) {
          cognitoUser.getSession(async (err: any, currentSession: CognitoUserSession | null) => {
            if (!err && currentSession) {
              await refreshSession(cognitoUser, currentSession);
            }
          });
        }
      }, refreshTime);
    }
  };

  // Initialize session on app startup
  useEffect(() => {
    // Debug configuration in development
    if (process.env.NODE_ENV === 'development') {
      debugCognitoConfig();
      testCognitoConnection();
    }
    
    validateAndRestoreSession();
  }, []);

  // Sign up function
  const signUp = async (userData: RegistrationFormData): Promise<{ success: boolean; message?: string }> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const attributeList = [
        new CognitoUserAttribute({ Name: 'email', Value: userData.email }),
        new CognitoUserAttribute({ Name: 'name', Value: userData.name }),
      ];

      if (userData.phoneNumber) {
        attributeList.push(new CognitoUserAttribute({ Name: 'phone_number', Value: userData.phoneNumber }));
      }

      return new Promise((resolve) => {
        userPool.signUp(
          userData.email,
          userData.password,
          attributeList,
          [],
          (err, result) => {
            dispatch({ type: 'SET_LOADING', payload: false });
            
            if (err) {
              const error = createAuthError(err, 'signUp');
              dispatch({ type: 'SET_ERROR', payload: error.message });
              resolve({ success: false, message: error.message });
            } else {
              resolve({ 
                success: true, 
                message: 'Registration successful! Please check your email to verify your account.' 
              });
            }
          }
        );
      });
    } catch (error) {
      const authError = createAuthError(error, 'signUp');
      dispatch({ type: 'SET_ERROR', payload: authError.message });
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, message: authError.message };
    }
  };

  // Sign in function
  const signIn = async (credentials: LoginFormData): Promise<{ success: boolean; message?: string }> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const cognitoUser = createCognitoUser(credentials.email);
      const authenticationDetails = createAuthenticationDetails(credentials.email, credentials.password);

      return new Promise((resolve) => {
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (session: CognitoUserSession) => {
            const user = extractUserFromSession(session, cognitoUser);
            
            // Store tokens
            storeTokens(session);
            
            // Schedule automatic token refresh
            scheduleTokenRefresh(session);
            
            // Update state
            dispatch({ type: 'SET_USER', payload: user });
            dispatch({ type: 'SET_TOKENS', payload: {
              accessToken: session.getAccessToken().getJwtToken(),
              idToken: session.getIdToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken()
            }});
            dispatch({ type: 'SET_LOADING', payload: false });
            
            resolve({ success: true });
          },
          onFailure: (err) => {
            const error = createAuthError(err, 'signIn');
            dispatch({ type: 'SET_ERROR', payload: error.message });
            dispatch({ type: 'SET_LOADING', payload: false });
            resolve({ success: false, message: error.message });
          }
        });
      });
    } catch (error) {
      const authError = createAuthError(error, 'signIn');
      dispatch({ type: 'SET_ERROR', payload: authError.message });
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, message: authError.message };
    }
  };

  // Sign out function
  const signOut = async (): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const cognitoUser = userPool.getCurrentUser();
      
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      
      // Clear stored tokens
      clearStoredTokens();
      
      // Clear state
      dispatch({ type: 'CLEAR_AUTH' });
    } catch (error) {
      console.warn('Error during sign out:', error);
      // Still clear state even if there's an error
      clearStoredTokens();
      dispatch({ type: 'CLEAR_AUTH' });
    }
  };

  // Forgot password function
  const forgotPassword = async (email: string): Promise<{ success: boolean; message?: string }> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const cognitoUser = createCognitoUser(email);

      return new Promise((resolve) => {
        cognitoUser.forgotPassword({
          onSuccess: () => {
            dispatch({ type: 'SET_LOADING', payload: false });
            resolve({ 
              success: true, 
              message: 'Password reset code sent to your email.' 
            });
          },
          onFailure: (err) => {
            const error = createAuthError(err, 'forgotPassword');
            dispatch({ type: 'SET_ERROR', payload: error.message });
            dispatch({ type: 'SET_LOADING', payload: false });
            resolve({ success: false, message: error.message });
          }
        });
      });
    } catch (error) {
      const authError = createAuthError(error, 'forgotPassword');
      dispatch({ type: 'SET_ERROR', payload: authError.message });
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, message: authError.message };
    }
  };

  // Confirm password function
  const confirmPassword = async (data: PasswordResetFormData): Promise<{ success: boolean; message?: string }> => {
    if (!data.email || !data.verificationCode || !data.newPassword) {
      return { success: false, message: 'Email, verification code, and new password are required.' };
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const cognitoUser = createCognitoUser(data.email);

      return new Promise((resolve) => {
        cognitoUser.confirmPassword(data.verificationCode!, data.newPassword!, {
          onSuccess: () => {
            dispatch({ type: 'SET_LOADING', payload: false });
            resolve({ 
              success: true, 
              message: 'Password reset successful! You can now log in with your new password.' 
            });
          },
          onFailure: (err) => {
            const error = createAuthError(err, 'confirmPassword');
            dispatch({ type: 'SET_ERROR', payload: error.message });
            dispatch({ type: 'SET_LOADING', payload: false });
            resolve({ success: false, message: error.message });
          }
        });
      });
    } catch (error) {
      const authError = createAuthError(error, 'confirmPassword');
      dispatch({ type: 'SET_ERROR', payload: authError.message });
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, message: authError.message };
    }
  };

  // Get current user function
  const getCurrentUser = async (): Promise<User | null> => {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        const user = extractUserFromSession(session, cognitoUser);
        resolve(user);
      });
    });
  };

  // Check if user is authenticated
  const checkAuthentication = (): boolean => {
    return state.isAuthenticated;
  };

  // Context value
  const contextValue: AuthContextType = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    tokens: state.tokens,
    
    // Methods
    signUp,
    signIn,
    signOut,
    forgotPassword,
    confirmPassword,
    getCurrentUser,
    checkAuthentication,
    clearError
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;